import React, { useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { ContentAdd, NoticeLoading } from '@fs/zion-icon'
import { Alert, Button, Checkbox, Divider, Separator, useStatusOverlay, Row } from '@fs/zion-ui'
import { useQuery } from '@apollo/client/react'

import { css } from '@emotion/core'
import { useCoverages, useOpenStates } from '../../../../hooks'
import Helpers from '../../helpers'
import EditableCoverage from '../editableCoverage/EditableCoverage'
import styles from '../../Coverages.styles'
import queries from '../../queries'
import transl from './EditableCoverages.transl'
import { useOverlayContext } from '../../../../../../../shared/providers/overlayContextProvider/OverlayContext'

const removeDefaultListItemStyles = css`
  li {
    list-style: none;
    margin: 0;
    padding: 0;
  }
`

export default function EditableCoverages({ forVolumeSet }) {
  const {
    addPlace,
    coverages,
    coverageCheckboxes,
    setAllCoverageCheckboxes,
    coverageDataMissing,
    validateCoverages,
    setAuditMissingCoveragesAsCompleted,
    savingMissingAuditData,
    updatedAuditCoverages,
  } = useCoverages({
    forVolumeSet,
  })
  const { closeOverlay, unsubscribe } = useOverlayContext()
  const { getIsOpen, handleToggleOpen, handleToggleOpenAll, allClosed } = useOpenStates(coverages)
  const { data: allRecordTypes, loading } = useQuery(queries.getRecordTypes)
  const [coverageErrors] = useState(validateCoverages())
  const missingCoverageCompleteStatusOverlay = useStatusOverlay()

  const percentChecked = useMemo(() => {
    let percentSum = 0
    let totalSum = 0
    coverageCheckboxes.forEach((recordTypeCheckboxes) => {
      percentSum += Helpers.getPercentRecordTypesTrue(recordTypeCheckboxes)
      totalSum += 1
    })
    return percentSum / totalSum
  }, [coverageCheckboxes])

  const saveAndClose = useCallback(async () => {
    await setAuditMissingCoveragesAsCompleted(missingCoverageCompleteStatusOverlay)
    closeOverlay('editMetadataOverlay')
    unsubscribe(() => 'editPanelOpen')
  }, [setAuditMissingCoveragesAsCompleted, closeOverlay, unsubscribe, missingCoverageCompleteStatusOverlay])

  const AlertBody = useCallback(() => {
    if ((!coverageDataMissing && !coverageErrors) || updatedAuditCoverages) return null

    const dataMissingList = coverageDataMissing.split('|')

    const header = coverageErrors ? transl.errorsHeader() : transl.noErrorsHeader()
    const list = coverageErrors ? dataMissingList : null
    let footer = transl.editAndSaveChanges()
    if (coverageDataMissing && !coverageErrors)
      footer = (
        <div css={styles.colorButton}>
          <Button
            emphasis="low"
            onClick={saveAndClose}
            disabled={savingMissingAuditData}
            Icon={savingMissingAuditData && NoticeLoading}
          >
            {transl.coverageDataIsComplete()}
          </Button>
        </div>
      )

    return (
      <>
        <>{header}</>
        <Separator size="xs" />
        {list?.length > 0 && (
          <ul>
            {list.map((item) => (
              <li key={item}>{transl[item]()}</li>
            ))}
          </ul>
        )}
        <Separator size="xs" />
        <>{footer}</>
      </>
    )
  }, [coverageDataMissing, saveAndClose, savingMissingAuditData, coverageErrors, updatedAuditCoverages])

  return loading ? null : (
    <>
      {coverageDataMissing && (
        <div css={styles.adjustIcon}>
          <Alert outlined type="warning">
            <AlertBody />
          </Alert>
        </div>
      )}
      {coverages?.length > 0 && (
        <>
          <Row alignY="middle">
            <Checkbox
              aria-label={transl.selectAllCoveragesLabel()}
              name="selectAll"
              checked={percentChecked === 1}
              mixedState={percentChecked > 0 && percentChecked < 1}
              onChange={({ target }) => setAllCoverageCheckboxes(target.checked)}
            />
            <div growX css={styles.placeCheckboxText(false)}>
              {transl.selectAll()}
            </div>
            <Button onClick={handleToggleOpenAll}>{allClosed ? transl.openAll() : transl.closeAll()}</Button>
          </Row>
          <Separator size="nano" />
          <Divider fullWidth aria-hidden />
        </>
      )}
      <ul css={removeDefaultListItemStyles}>
        {coverages?.map(({ place, recordTypes }, index) => (
          <React.Fragment key={place?.id}>
            <EditableCoverage
              forVolumeSet={forVolumeSet}
              place={place}
              placeIndex={index}
              recordTypes={recordTypes}
              handleToggleOpen={handleToggleOpen}
              open={getIsOpen(place?.id)}
              allRecordTypes={allRecordTypes?.recordTypes || []}
            />
          </React.Fragment>
        ))}
      </ul>

      <Separator size="xxs" />
      <Button Icon={ContentAdd} onClick={addPlace}>
        {transl.placeLabel()}
      </Button>
      <Separator size="xxs" />
      <Divider />
    </>
  )
}

EditableCoverages.propTypes = {
  forVolumeSet: PropTypes.bool,
}
